import type {
  DataErrorCode,
  Grants,
  JsonObject,
  MemberRole,
  MenuOverrides,
  RealtimeStatus,
  RecordDoc,
  TaskScoreMap,
} from '../lib/supabase';

/** Account shape the legacy UI (public/legacy/app.js) works with. `id` is the login email. */
export interface LegacyUser {
  id: string;
  uid: string;
  email: string;
  name: string;
  empCode: string;
  role: MemberRole;
  active: boolean;
  grants: Grants;
  menus: MenuOverrides | null;
}

export interface LegacyAuditRow extends JsonObject {
  id: number;
  at: string;
  who: string;
  whoName: string;
  action: string;
}

export type SignInResult =
  | { readonly ok: true; readonly role: MemberRole }
  | {
      readonly ok: false;
      readonly reason: 'bad_credentials' | 'inactive' | 'rate_limited' | 'no_workspace' | 'network' | 'unknown';
      readonly message: string;
    };

export type RemoteChange =
  | { readonly kind: 'record'; readonly module: string; readonly id: string; readonly change: 'created' | 'updated' | 'deleted'; readonly byMe: boolean }
  | { readonly kind: 'settings' }
  | { readonly kind: 'members'; readonly selfRemoved: boolean }
  | { readonly kind: 'resync' }
  | { readonly kind: 'realtime'; readonly status: RealtimeStatus }
  | { readonly kind: 'signed_out' };

export interface PullResult {
  created: number;
  updated: number;
  deleted: number;
  conflicts: number;
}

export interface LegacyLocalData {
  readonly count: number;
  readonly config: JsonObject | null;
  readonly modules: Record<string, JsonObject[]>;
}

export interface CoreInfo {
  readonly supabaseUrl: string;
  readonly appKey: string;
  readonly workspace: { readonly id: string; readonly slug: string; readonly name: string } | null;
  /** Every company this user may open, each with its own link (two-company separation). */
  readonly workspaces: ReadonlyArray<{
    readonly id: string;
    readonly slug: string;
    readonly name: string;
    readonly url: string;
  }>;
  readonly email: string | null;
  readonly role: MemberRole | null;
  readonly realtime: RealtimeStatus;
}

/** Error thrown to legacy code on failed writes; legacy JS branches on `code`. */
export class LegacyWriteError extends Error {
  readonly code: DataErrorCode;
  readonly server: RecordDoc | null;

  constructor(code: DataErrorCode, message: string, server: RecordDoc | null = null) {
    super(message);
    this.name = 'LegacyWriteError';
    this.code = code;
    this.server = server;
  }
}

export interface LegacyRecordsApi {
  modules(): string[];
  read(module: string): RecordDoc[];
  put(module: string, record: unknown): void;
  drop(module: string, id: string): void;
  seed(module: string, rows: unknown): void;
  upsert(module: string, record: unknown): Promise<RecordDoc>;
  remove(module: string, id: string): Promise<void>;
  pull(module: string): Promise<PullResult>;
  importRows(module: string, rows: unknown): Promise<number>;
  purgeDemo(): Promise<number>;
  resetAll(): Promise<number>;
}

/** Attachments live in Storage (RLS-gated like records.* by module), not inline in record JSON. */
export interface LegacyFilesApi {
  upload(module: string, file: File): Promise<{ path: string; name: string; type: string; size: number }>;
  signedUrl(path: string, downloadName?: string): Promise<string>;
  remove(path: string): Promise<void>;
}

export interface LegacyAdminApi {
  createUser(input: unknown): Promise<{ userId: string; existingUser: boolean }>;
  setRole(uid: string, role: unknown): Promise<void>;
  setPassword(uid: string, password: string): Promise<void>;
  setActive(uid: string, active: boolean): Promise<void>;
  deleteUser(uid: string): Promise<void>;
}

export interface LuviaCoreApi {
  readonly appKey: string;
  info(): CoreInfo;
  bootError(): string | null;
  currentUser(): LegacyUser | null;
  users(): LegacyUser[];
  settings(): JsonObject;
  saveSettings(config: unknown): Promise<void>;
  saveUsers(users: unknown): Promise<void>;
  signIn(email: string, password: string): Promise<SignInResult>;
  /** Redirects to Google; the browser leaves the page and comes back signed in. */
  signInWithGoogle(): Promise<void>;
  signOut(): Promise<void>;
  /** Switches this browser to another company the user belongs to; caller reloads on true. */
  switchWorkspace(slug: string): Promise<boolean>;
  sendPasswordReset(email: string): Promise<void>;
  ping(): Promise<number>;
  admin: LegacyAdminApi;
  records: LegacyRecordsApi;
  files: LegacyFilesApi;
  audit: { rows(): LegacyAuditRow[]; push(row: unknown): void; remove(ids: number[]): Promise<void> };
  /** Stage 5 (Approved) scores, keyed by task id — admin/manager only (RLS enforced). */
  scores(): TaskScoreMap;
  saveScore(taskId: string, score: number, comment: string): Promise<void>;
  refreshScores(): Promise<void>;
  realtime: { start(records: boolean): void; stop(): void; status(): RealtimeStatus };
  legacyLocalData(): LegacyLocalData | null;
  importLegacyLocalData(): Promise<number>;
  clearLegacyLocalData(): void;
  onRemoteChange(handler: (change: RemoteChange) => void): void;
}

declare global {
  interface Window {
    LuviaCore?: LuviaCoreApi;
  }
}
